// Focus-trap helpers. These are the standard Dawn utilities, which this theme
// never bundled — define them here (guarded) so cart-notification's open()/close()
// don't throw "trapFocus is not defined" / "removeTrapFocus is not defined".
if (typeof window.trapFocus === "undefined") {
  const focusableSelector =
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';
  let trapFocusHandlers = {};

  window.trapFocus = function (container, elementToFocus = container) {
    const elements = Array.from(container.querySelectorAll(focusableSelector));
    const first = elements[0];
    const last = elements[elements.length - 1];

    window.removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
        return;
      document.addEventListener("keydown", trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
      document.removeEventListener("keydown", trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = (event) => {
      if (event.code !== "Tab") return;

      // If tab pressed on last element, focus first
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        if (first) first.focus();
      }

      // If shift+tab pressed on first element (or container), focus last
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        if (last) last.focus();
      }
    };

    document.addEventListener("focusout", trapFocusHandlers.focusout);
    document.addEventListener("focusin", trapFocusHandlers.focusin);

    if (elementToFocus) elementToFocus.focus();
  };

  window.removeTrapFocus = function (elementToFocus = null) {
    document.removeEventListener("focusin", trapFocusHandlers.focusin);
    document.removeEventListener("focusout", trapFocusHandlers.focusout);
    document.removeEventListener("keydown", trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
  };
}

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById("cart-notification");
    this.header = document.querySelector("sticky-header");
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener("click", this.close.bind(this)),
    );

    // Event delegation for dynamically added close buttons (e.g. "Weiter shoppen")
    this.notification.addEventListener("click", (e) => {
      if (e.target.closest(".close_btn")) {
        this.close();
      }
    });

    // Initialize add-to-cart interception for cart notification popup
    this.initFormInterception();
  }

  /**
   * Intercepts all .Sd_addProduct button clicks (product cards, quick-add, etc.)
   * Uses capture phase to fire BEFORE the jQuery handler in cart-ajax.js,
   * preventing the cart drawer from opening and showing the notification instead.
   */
  initFormInterception() {
    document.addEventListener(
      "click",
      (e) => {
        const submitBtn = e.target.closest(".Sd_addProduct");
        if (!submitBtn) return;

        const form = submitBtn.closest("form");
        if (!form) return;

        // Prevent default form submission and stop propagation to jQuery handlers
        e.preventDefault();
        e.stopPropagation();

        submitBtn.classList.add("is-loading");

        // The variant id in the product form is written asynchronously once the
        // section fetch returns, so a fast click can serialise a superseded id and
        // add the wrong size. Re-derive it from the options that are actually
        // checked at this moment before building the payload.
        try {
          const productInfo = form.closest("product-info");
          const variantSelects = productInfo?.querySelector("variant-selects");
          if (variantSelects && typeof productInfo.syncVariantInputFromLabels === "function") {
            productInfo.syncVariantInputFromLabels(variantSelects.selectedOptionLabels);
          }
        } catch (_) {}

        const formData = new FormData(form);

        fetch((window.Shopify?.routes?.root || "/") + "cart/add.js", {
          method: "POST",
          body: formData,
        })
          .then((res) => {
            if (!res.ok) {
              return res.json().then((data) => {
                throw new Error(
                  data.description || "Could not add item to cart",
                );
              });
            }
            return res.json();
          })
          .then((item) => {
            submitBtn.classList.remove("is-loading");

            // Signal to quick-add (and any other UI) that the add-to-cart
            // completed, so their loaders can hide immediately.
            document.dispatchEvent(
              new CustomEvent("cart:add-complete", { detail: { item } }),
            );

            // Close quick-add popup if open
            const quickAddPopup = form.closest(".quick-add-popup");
            if (quickAddPopup) {
              quickAddPopup.classList.remove("show");
              const quickAddBody = form.closest("[quick-add-body]");
              if (quickAddBody) quickAddBody.innerHTML = "";
              document.body.classList.remove("quick-add-popup-open");
            }

            // Close quickview if open
            document.body.classList.remove("quickview-open");

            // Play add to cart sound if available
            const addToCartAlert = document.getElementById("addToCartAlert");
            if (addToCartAlert) addToCartAlert.play();

            // Haptic feedback
            if (
              typeof hapticFeedbackStatus !== "undefined" &&
              hapticFeedbackStatus
            ) {
              if (window.navigator.vibrate) window.navigator.vibrate(500);
            }

            // Fetch cart data for shipping bar and cart count, then render notification
            fetch((window.Shopify?.routes?.root || "/") + "cart.js")
              .then((r) => r.json())
              .then((cart) => {
                this.renderItemNotification(item, cart);
                this.updateCartCountFromData(cart);
              })
              .catch(() => {
                // Fallback: render without cart data (no shipping bar)
                this.renderItemNotification(item, null);
              });
          })
          .catch((err) => {
            console.error("Error adding to cart:", err);
            submitBtn.classList.remove("is-loading");
            document.dispatchEvent(new CustomEvent("cart:add-complete"));

            const parentSection = form.closest(".shopify-section");
            if (parentSection) {
              const errorContainer =
                parentSection.querySelector(".productErrors");
              if (errorContainer) {
                errorContainer.textContent =
                  err.message || "Could not add item to cart";
                errorContainer.style.display = "";
                errorContainer.style.visibility = "visible";
              }
            }
          });
      },
      true, // capture phase — fires before jQuery bubbling handlers
    );
  }

  /**
   * Builds the shipping progress bar HTML from live cart data.
   */
  buildShippingBar(cart) {
    if (!cart) return "";

    // Check if free shipping bar is enabled
    if (
      typeof freeShippingBarStatus !== "undefined" &&
      !freeShippingBarStatus
    ) {
      return "";
    }

    // Get the shipping threshold (in cents)
    const shippingThreshold =
      typeof convertShippingAmount !== "undefined"
        ? convertShippingAmount
        : typeof freeShippingBarAmount !== "undefined"
          ? freeShippingBarAmount
          : 0;

    if (shippingThreshold <= 0) return "";

    const totalPrice = cart.items_subtotal_price || 0;
    const remaining = shippingThreshold - totalPrice;
    const percentage = Math.min(
      100,
      Math.round((totalPrice * 100) / shippingThreshold),
    );

    let messageHtml = "";
    if (remaining > 0) {
      let remainingFormatted = "CHF " + (remaining / 100).toFixed(2);
      try {
        if (typeof Shopify.formatMoney === "function") {
          remainingFormatted = Shopify.formatMoney(
            remaining,
            typeof moneyFormat !== "undefined" ? moneyFormat : "CHF {{amount}}",
          );
        }
      } catch (e) {
        /* use fallback */
      }

      // Translatable text. Prefer the merchant's shipping_text setting if set;
      // otherwise fall back to the localized theme string (general.free_shipping_remaining_html),
      // then to a hardcoded German string as a last resort.
      let shippingText;
      if (typeof freeShippingBarText !== "undefined" && freeShippingBarText) {
        shippingText = freeShippingBarText.replace(
          "||amount||",
          remainingFormatted,
        );
      } else if (
        typeof freeShippingRemainingText !== "undefined" &&
        freeShippingRemainingText
      ) {
        shippingText = freeShippingRemainingText.replace(
          "||amount||",
          remainingFormatted,
        );
      } else {
        shippingText = `Noch ${remainingFormatted} bis zum kostenlosen Versand`;
      }

      messageHtml = `
        <p class="totals__total-value total-less">
          <span class="af-freeshipping-text">${shippingText}           <span class="icon_wrap">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="20" height="15" fill="currentColor"><path d="M48 0C21.5 0 0 21.5 0 48V368c0 26.5 21.5 48 48 48H64c0 53 43 96 96 96s96-43 96-96H384c0 53 43 96 96 96s96-43 96-96h32c17.7 0 32-14.3 32-32s-14.3-32-32-32V288 256 237.3c0-17-6.7-33.3-18.7-45.3L512 118.6c-12-12-28.3-18.7-45.3-18.7H416V48c0-26.5-21.5-48-48-48H48zM416 160h50.7L544 237.3V256H416V160zM112 416a48 48 0 1 1 96 0 48 48 0 1 1-96 0zm368-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/></svg>
          </span></span>
        </p>`;
    } else {
      // Translatable success text. Prefer the merchant's free_shipping_text setting;
      // otherwise fall back to the localized theme string, then to German.
      let successText;
      if (
        typeof freeShippingBarSuccessText !== "undefined" &&
        freeShippingBarSuccessText
      ) {
        successText = freeShippingBarSuccessText;
      } else if (
        typeof freeShippingSuccessTextLabel !== "undefined" &&
        freeShippingSuccessTextLabel
      ) {
        successText = freeShippingSuccessTextLabel;
      } else {
        successText = "Gut gemacht.<br/> Ab jetzt ist dein Versand kostenlos";
      }

      messageHtml = `
        <p class="totals__total-value progress-text">
          <span class="af-freeshipping-text">${successText} <span class="icon_wrap"><img src="https://cdn.shopify.com/s/files/1/0694/0681/8529/files/party-hat-icon.jpg?v=1747391330" width="15" height="auto"></span></span>
        </p>`;
    }

    return `
      <div class="totals shipping_bar_wrap">
        <div class="shipping_progress_bar" style="--progressbar-width: ${percentage}%;"></div>
        ${messageHtml}
      </div>`;
  }

  /**
   * Builds the notification popup content from the line item returned by /cart/add.js
   * and the current cart state from /cart.js.
   */
  renderItemNotification(item, cart) {
    const productEl = document.getElementById("cart-notification-product");
    if (!productEl) return;

    // Format price
    let priceHtml = "";
    try {
      priceHtml =
        typeof Shopify.formatMoney === "function"
          ? Shopify.formatMoney(
              item.price,
              typeof moneyFormat !== "undefined"
                ? moneyFormat
                : "CHF {{amount}}",
            )
          : "CHF " + (item.price / 100).toFixed(2);
    } catch (e) {
      priceHtml = "CHF " + (item.price / 100).toFixed(2);
    }

    // Get image URL
    let imageUrl = "";
    if (item.featured_image && item.featured_image.url) {
      imageUrl = item.featured_image.url;
    } else if (item.image) {
      imageUrl = item.image;
    }

    // Build variant info — "Variante: variant_title" (label translatable)
    const variantLbl =
      typeof variantLabel !== "undefined" && variantLabel
        ? variantLabel
        : "Variante";
    let variantHtml = "";
    if (item.variant_title && item.variant_title !== "Default Title") {
      variantHtml = `<dl><dd class="product-option">${variantLbl}: ${item.variant_title}</dd></dl>`;
    }

    // Build quantity info — "Anzahl: quantity" (label translatable)
    const quantityLbl =
      typeof quantityLabel !== "undefined" && quantityLabel
        ? quantityLabel
        : "Anzahl";
    const quantityHtml = `<dl><dd class="product-option">${quantityLbl}: ${item.quantity}</dd></dl>`;

    // AICO Vorverkauf: the product page already rendered the translated, styled
    // advance-sale banner for flagged products — mirror that node into the popup.
    // Unflagged product (or quick-add outside the product page) has no such node,
    // so this stays inert.
    const advanceSaleEl = document.querySelector("[data-advance-sale-banner]");
    const advanceSaleHtml = advanceSaleEl ? advanceSaleEl.outerHTML : "";

    // Build shipping progress bar
    const shippingBarHtml = this.buildShippingBar(cart);

    // Build the notification product HTML
    productEl.innerHTML = `
      <div id="cart-notification-product-${item.key}" class="cart-notification-wrapper">
        ${
          imageUrl
            ? `
          <a class="cart-notification-product__image" href="${item.url}">
            <img src="${imageUrl}" alt="${item.product_title}" loading="lazy">
          </a>
        `
            : ""
        }
        <div class="cart-notification-product__info">
          <a href="${item.url}" class="cart-notification-product__name">${item.product_title}</a>

          <div class="cart-notification-price" data_compare_price="${item.compare_at_price || ""}">
            <dd class="product-option">${priceHtml}</dd>
          </div>

          ${variantHtml}
          ${quantityHtml}
          ${advanceSaleHtml}
        </div>
        <div class="cart-notification__links">
          ${shippingBarHtml}
          
          <div class="cart-notification__links-buttons">
          <a href="/cart" class="button button--secondary button--full-width">
            ${
              typeof cartViewLabel !== "undefined" && cartViewLabel
                ? cartViewLabel
                : "Warenkorb ansehen"
            }
          </a>
          <button type="button" class="button button-label button--full-width close_btn">
            ${
              typeof continueShoppingLabel !== "undefined" &&
              continueShoppingLabel
                ? continueShoppingLabel
                : "Weiter shoppen"
            }
          </button>
          </div>
        </div>
      </div>
    `;

    // Apply AICO discount if applicable
    if (
      window.aico_percentage_discount &&
      window.aico_percentage_discount != 0 &&
      typeof applyAicoDiscount === "function"
    ) {
      const priceElement = productEl.querySelector(
        ".cart-notification-price .product-option",
      );
      if (priceElement) {
        const priceValue = item.price / 100;
        let discountedPrice;
        let originalPrice = priceHtml;

        if (item.compare_at_price) {
          const comparePriceValue = item.compare_at_price / 100;
          const compareDiscountPrice = applyAicoDiscount(comparePriceValue);

          if (compareDiscountPrice < priceValue) {
            discountedPrice = compareDiscountPrice;
            originalPrice = `CHF ${comparePriceValue.toFixed(2)}`;
          } else {
            discountedPrice = priceValue.toFixed(2);
            originalPrice = `CHF ${comparePriceValue.toFixed(2)}`;
          }
        } else {
          discountedPrice = applyAicoDiscount(priceValue);
        }

        priceElement.innerHTML = `<span class="af-strike-price">${originalPrice}</span><span class="af-discount-price"><span class="af-discount-text">DEIN PREIS:</span><span class="af-discount-price-inner">CHF ${discountedPrice}</span></span>`;
      }
    }

    // Process related products
    this.processCartRelatedProducts();

    if (this.header) this.header.reveal();
    this.open();
  }

  /**
   * Updates cart count badges from already-fetched cart data (avoids duplicate fetch).
   */
  updateCartCountFromData(cart) {
    if (!cart) return;

    const displayCount =
      typeof getDisplayItemCount === "function"
        ? getDisplayItemCount(cart)
        : cart.item_count;

    // Update all cart count elements
    const cartCounts = document.querySelectorAll("[data-cart-count]");
    cartCounts.forEach((el) => {
      const numEl = el.querySelector(".cart-count-badge__number") || el;
      if (displayCount > 0) {
        el.classList.remove("cart-count-badge--hidden");
        numEl.textContent = displayCount < 100 ? displayCount : "";
      } else {
        el.classList.add("cart-count-badge--hidden");
        numEl.textContent = "";
      }
    });

    // Update favicon badge if available
    if (
      typeof favicon !== "undefined" &&
      favicon &&
      typeof favicon.badge === "function"
    ) {
      favicon.badge(displayCount);
    }

    // Update global cart count variable
    if (typeof cartItemsCount !== "undefined") {
      window.cartItemsCount = displayCount;
    }
  }

  open() {
    this.notification.classList.add("animate", "active");

    this.notification.addEventListener(
      "transitionend",
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true },
    );

    document.body.addEventListener("click", this.onBodyClick);
  }

  close() {
    this.notification.classList.remove("active");
    document.body.removeEventListener("click", this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  /**
   * Renders notification content from pre-rendered section HTML (used by Shopify Section Rendering API).
   * Kept for backward compatibility.
   */
  renderContents(parsedState) {
    if (parsedState.key) {
      this.cartItemKey = parsedState.key;
    } else {
      let item_key_index = parsedState.items.length - 1;
      this.cartItemKey = parsedState.items[item_key_index].key;
    }

    this.getSectionsToRender().forEach((section) => {
      if (section) {
        let sectionElement = document.getElementById(section.id);
        if (sectionElement) {
          sectionElement.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector,
          );
        } else {
          console.warn(`Element with ID '${section.id}' not found.`);
        }
      }
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-notification-product",
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    let parsedDoc = new DOMParser().parseFromString(html, "text/html");
    let sectionElement = parsedDoc.querySelector(selector);

    if (sectionElement) {
      if (
        window.aico_percentage_discount &&
        window.aico_percentage_discount != 0
      ) {
        let priceValueNew = 0;
        let checkComparePrice = sectionElement.querySelector(
          ".cart-notification-price",
        );

        if (checkComparePrice) {
          var comparePriceAttr =
            checkComparePrice.getAttribute("data_compare_price");
          console.log("comparePriceAttr11", comparePriceAttr);
        } else {
          console.log("checkComparePrice element not found");
        }

        let priceElement = sectionElement.querySelector(
          ".cart-notification-price .product-option",
        );
        let priceElementTrikot = sectionElement.querySelector(
          ".cart-notification-price .price",
        );
        const oldPriceElements = document.querySelectorAll(
          ".cart-item__old-price",
        );
        oldPriceElements.forEach((element) => element.remove());
        if (priceElementTrikot) {
          priceElement = priceElementTrikot;
        }
        console.log("priceElement", priceElement);

        if (priceElement) {
          let priceText = priceElement.textContent.trim();
          let priceValue = parseFloat(
            priceText.replace(/[^\d,.]/g, "").replace(",", "."),
          );
          console.log("Price value as float:", priceValue);

          let discountedPrice;
          let originalPrice = priceText;

          if (comparePriceAttr) {
            let comparePriceValue = comparePriceAttr / 100;
            console.log(
              "Using compare_at_price for discount:",
              comparePriceValue,
            );
            let compareDiscountPrice = applyAicoDiscount(comparePriceValue);

            if (compareDiscountPrice < priceValue) {
              console.log(
                "Using discounted price (better for customer):",
                compareDiscountPrice,
              );
              discountedPrice = compareDiscountPrice;
              originalPrice = `CHF ${comparePriceValue.toFixed(2)}`;
            } else {
              console.log(
                "Using current price (better for customer):",
                priceValue,
              );
              discountedPrice = priceValue.toFixed(2);
              originalPrice = `CHF ${comparePriceValue.toFixed(2)}`;
            }
          } else {
            console.log("Using current price for discount:", priceValue);
            discountedPrice = applyAicoDiscount(priceValue);
            originalPrice = priceText;
          }

          if (priceValueNew) {
            let discountedPriceNew = (
              parseFloat(discountedPrice) + parseFloat(priceValueNew)
            ).toFixed(2);
            discountedPrice = discountedPriceNew;
            console.log("discountedPriceNew", discountedPriceNew);
          }
          console.log("Original price for strikethrough:", originalPrice);
          let strikePriceElement = parsedDoc.querySelector(
            ".cart-notification-price .af-strike-price",
          );
          console.log("strikePriceElement", strikePriceElement);
          if (strikePriceElement) {
            strikePriceElement.innerHTML = originalPrice;
          } else {
            console.log(".af-strike-price element not found.");
          }
          console.log("cart-notification");

          priceElement.innerHTML = `<span class="af-discount-price"><span class="af-discount-text">DEIN PREIS:</span><span class="af-discount-price-inner">CHF ${discountedPrice}</span></span>`;
        } else {
          console.warn(".product-option price element not found in section");
        }
      }
      this.processCartRelatedProducts();
      return sectionElement.innerHTML;
    } else {
      console.warn(`Selector '${selector}' not found in parsed HTML.`);
      return "";
    }
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest("cart-notification")) {
      const disclosure = target.closest("details-disclosure, header-menu");
      this.activeElement = disclosure
        ? disclosure.querySelector("summary")
        : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  processCartRelatedProducts() {
    // Guard: only run if jQuery is available
    if (typeof $ === "undefined" && typeof jQuery === "undefined") return false;
    const _$ = typeof $ !== "undefined" ? $ : jQuery;

    const relatedProducts = _$(
      ".cart-notification-product .connected_products_wrapper li",
    ).not('[data-processed="true"]');

    if (relatedProducts.length) {
      relatedProducts.each(function () {
        const li = _$(this);
        const span = li.find("span#aico-price-list");

        if (span.length) {
          const dataAttr = span.attr("data-aico_price_object");

          if (dataAttr) {
            try {
              const dataObj = JSON.parse(dataAttr);

              if (
                window.aico_percentage_discount &&
                window.aico_percentage_discount != 0
              ) {
                let amount = 0;
                let originalPrice;

                if (dataObj.compare_price) {
                  let comparePriceValue = dataObj.compare_price / 100;
                  let compareDiscountPrice =
                    applyAicoDiscount(comparePriceValue);
                  let currentPriceValue = dataObj.price / 100;

                  if (compareDiscountPrice < currentPriceValue) {
                    amount = compareDiscountPrice;
                    originalPrice = `CHF ${comparePriceValue.toFixed(2)}`;
                  } else {
                    amount = currentPriceValue;
                    originalPrice = `CHF ${comparePriceValue.toFixed(2)}`;
                  }
                } else {
                  amount = applyAicoDiscount(dataObj.price);
                  originalPrice = `CHF ${parseFloat(dataObj.price).toFixed(2)}`;
                }

                li.find(".af-strike-price").html(originalPrice);
                console.log("cart-notification 1");
                li.find(".af-price")
                  .html(
                    `<span class="af-discount-text">DEIN PREIS:</span><span class="af-discount-price-inner">CHF ${amount}</span>`,
                  )
                  .addClass("af-discount-price");
                li.find(
                  ".card_options_form form #price_item_discount",
                ).remove();
                li.find(".card_options_form form ").append(
                  `<input id="price_item_discount" type="hidden" name="properties[_price_item_discount]" value="${amount}">`,
                );
                li.attr("data-processed", "true");
              }
            } catch (e) {
              console.error("Invalid JSON in data-aico_price_object", e);
            }
          }
        }
      });

      return true;
    } else {
      return false;
    }
  }
}

customElements.define("cart-notification", CartNotification);

// jQuery handler: clicking "Weiter shoppen" (.close_btn) triggers the modal close button
$(document).ready(function () {
  $(document).on("click", ".cart-notification__links .close_btn", function () {
    $(".modal__close-button").trigger("click");
  });
});
